/**
 * Sample Loader
 * Fetches and decodes audio files for sample playback
 */

import { hashString, hashAudioBuffer } from './bytecode.ts'

export interface SampleData {
  id: number
  path: string
  audioBuffer: AudioBuffer
}

export class SampleLoader {
  private audioContext: AudioContext
  private loadedSamples = new Map<number, SampleData>()
  private loadingPromises = new Map<string, Promise<SampleData>>()
  private pathToContentHash = new Map<string, number>()

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext
  }

  async loadSample(path: string): Promise<SampleData> {
    // Check if we already have a loading promise for this path
    if (this.loadingPromises.has(path)) {
      return this.loadingPromises.get(path)!
    }

    // Start loading
    const promise = this.fetchAndDecodeSample(path)
    this.loadingPromises.set(path, promise)

    try {
      const sampleData = await promise
      // Cache by content hash, not path hash
      this.loadedSamples.set(sampleData.id, sampleData)
      this.pathToContentHash.set(path, sampleData.id)
      this.loadingPromises.delete(path)
      return sampleData
    } catch (error) {
      this.loadingPromises.delete(path)
      throw error
    }
  }

  private async fetchAndDecodeSample(path: string): Promise<SampleData> {
    // Resolve espeak URLs - replace __MESPEAK__ with actual env var URL
    let resolvedPath = path
    if (path.startsWith('__MESPEAK__')) {
      const baseUrl = import.meta.env.VITE_MESPEAK_URL || '/mespeak'
      resolvedPath = path.replace('__MESPEAK__', baseUrl)
    }

    try {
      // Fetch the audio file
      const response = await fetch(resolvedPath)
      if (!response.ok) {
        // Check if this is a freesound sample and extract the ID
        if (resolvedPath.includes('freesound')) {
          const idMatch = resolvedPath.match(/[?&]id=(\d+)/)
          const sampleId = idMatch ? idMatch[1] : 'unknown'
          const statusText = response.status === 404 ? 'Not found: 404' : `${response.status}`
          throw new Error(`Failed to fetch freesound sample "${sampleId}" (${statusText})`)
        }
        const statusText = response.status === 404 ? 'Not found: 404' : `${response.status}`
        throw new Error(`Failed to fetch sample: ${resolvedPath} (${statusText})`)
      }

      const arrayBuffer = await response.arrayBuffer()

      // Decode audio data
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer)

      // Compute content hash from audio data
      const contentHash = hashAudioBuffer(audioBuffer)

      return {
        id: contentHash,
        path: resolvedPath,
        audioBuffer,
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      // Check if this is a freesound sample and format the error message accordingly
      if (resolvedPath.includes('freesound')) {
        const idMatch = resolvedPath.match(/[?&]id=(\d+)/)
        const sampleId = idMatch ? idMatch[1] : 'unknown'
        // If the error message already mentions freesound, use it as-is
        if (errorMessage.toLowerCase().includes('freesound')) {
          throw error
        }
        throw new Error(`Failed to fetch freesound sample "${sampleId}": ${errorMessage}`)
      }
      throw new Error(`Failed to load sample "${resolvedPath}": ${errorMessage}`)
    }
  }

  getLoadedSample(path: string): SampleData | undefined {
    const contentHash = this.pathToContentHash.get(path)
    if (contentHash !== undefined) {
      return this.loadedSamples.get(contentHash)
    }
    return undefined
  }

  clear(): void {
    this.loadedSamples.clear()
    this.loadingPromises.clear()
    this.pathToContentHash.clear()
  }
}
